import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { ConfirmService } from '@core/services/confirm.service';
import { OsButtonDirective } from '../os-button/os-button.directive';
import { OsFieldComponent } from '../os-field/os-field.component';
import { OsInputComponent } from '../os-input/os-input.component';
import { OsSheetComponent } from '../os-sheet/os-sheet.component';

/**
 * Desenha as perguntas do `ConfirmService`. Fica uma vez só, na raiz do app.
 */
@Component({
	selector: 'os-confirm-host',
	imports: [OsSheetComponent, OsButtonDirective, OsFieldComponent, OsInputComponent],
	template: `
		<os-sheet
			[open]="!!request()"
			[heading]="request()?.heading ?? null"
			[dismissable]="true"
			(closed)="confirm.answer(false)"
			position="auto-center"
			size="sm"
		>
			@if (request()?.message; as text) {
				<p class="os-confirm__message">{{ text }}</p>
			}
			@if (request()?.typeToConfirm; as word) {
				<os-field class="os-confirm__type" [label]="'Para confirmar, digite ' + word" for="os-confirm-type">
					<os-input [value]="typed()" (valueChange)="typed.set($event)" inputId="os-confirm-type" autocomplete="off" />
				</os-field>
			}
			<div class="os-confirm__actions">
				<button (click)="confirm.answer(false)" osButton="secondary" type="button">
					{{ request()?.cancelLabel ?? 'Cancelar' }}
				</button>
				<button
					[osButton]="danger() ? 'danger' : 'primary'"
					[disabled]="!unlocked()"
					(click)="confirm.answer(true)"
					type="button"
				>
					{{ request()?.confirmLabel }}
				</button>
			</div>
		</os-sheet>
	`,
	styles: `
		:host {
			display: contents;
		}

		.os-confirm__message {
			margin: 0 0 20px;
			color: var(--os-ink-soft);
			font-size: var(--os-fs-15);
		}

		.os-confirm__type {
			display: block;
			margin-bottom: 20px;
		}

		.os-confirm__actions {
			display: flex;
			justify-content: flex-end;
			gap: var(--os-space-2);
		}

		@media (max-width: 760px) {
			.os-confirm__actions {
				flex-direction: column-reverse;

				button {
					width: 100%;
					height: var(--os-control-lg);
				}
			}
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OsConfirmHostComponent {
	readonly confirm = inject(ConfirmService);
	readonly request = this.confirm.current;
	readonly danger = computed(() => this.request()?.tone === 'danger');

	/** O que a pessoa digitou, quando a pergunta pede uma palavra. */
	readonly typed = signal('');
	readonly unlocked = computed(() => {
		const word = this.request()?.typeToConfirm;
		return !word || this.typed().trim().toUpperCase() === word.toUpperCase();
	});

	constructor() {
		/* Pergunta nova começa sem nada digitado. */
		effect(() => {
			this.request();
			this.typed.set('');
		});
	}
}
